<template>
  <div>
    <div>
      <div class="tw-flex tw-justify-between tw-mb-5">
        <div class="tw-flex tw-flex-col">
          <div class="tw-flex">
            <div class="tw-text-gray-800 tw-font-normal">{{ prescription.businessProgram.name }}</div>
            <base-badge class="tw-ml-2 tw-my-auto" :color="statusColor(status)" size="sm">{{ getPrescriptionStatus(status) }}</base-badge>
            <base-badge v-if="prescription.isMerge == true" class="tw-ml-2" size="sm" color="gray">Merged</base-badge>
          </div>
          <div class="tw-text-gray-600 tw-text-sm tw-mt-1">{{ getLocation(prescription) }}</div>
          <div class="tw-flex-col tw-mt-2">
            <div class="tw-text-xs tw-font-medium tw-text-gray-800">Submitted</div>
            <div class="tw-my-auto tw-text-sm tw-text-gray-600">{{ formatDate(prescription.submittedDate) }}</div>
          </div>
          <div v-if="prescription.documentCount > 1" class="tw-flex-col tw-mt-2">
            <div class="tw-text-xs tw-font-medium tw-text-gray-800">Renewed</div>
            <div class="tw-my-auto tw-text-sm tw-text-gray-600">{{ formatDate(prescription.renewedDate) }}</div>
          </div>
        </div>
        <base-dropdown v-if="this.prescription.isDeleted === false" class="tw-my-auto" :options="actionOptions" :showBackground="false" align="right">
          <base-icon>
            <more-vertical-icon class="tw-w-6 tw-h-6" />
          </base-icon>
          <template #option="{ option: label }">
            <button class="tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 focus:tw-outline-none" @click="actionLink(label)">
              {{ label }}
            </button>
          </template>
        </base-dropdown>
      </div>
      <div>
        <edit-business-member-prescription-medications
          v-if="isEditing"
          :edit-medications.sync="editMedications"
          :allowed-medications="allowedMedications"
          :allowedRxItems="allowedRxItems"
          :max-medications="prescription.businessProgram.maxMedications"
          :medications-label="dynamicLabel"
          @cancel="cancelEditing"
          @save="saveChanges"
          :is-saving="isSaving"
        />
        <div v-else class="tw-pb-2">
          <div v-for="(medication, index) in orderedMedications" :key="index" class="tw-mb-3">
            <div class="tw-text-xs tw-font-medium tw-text-gray-800">{{ dynamicLabel }} {{ index + 1 }}</div>
            <div class="tw-text-sm tw-text-gray-600">{{ medication.businessItemCategory.name.value }}</div>
          </div>
        </div>
        <div v-if="isEditing && allowedRxItems.length != 0">
          <edit-business-member-prescription-rx-items
            :edit-rx-items.sync="editRxItems"
            :allowed-rx-items="allowedRxItems"
            @cancel="cancelEditing"
            @save="saveChanges"
            :is-saving="isSaving"
          />
        </div>
        <div class="tw-flex tw-justify-end tw-mb-4 tw-gap-4" v-if="isEditing">
          <base-button size="sm" type="flat" class="tw-ml-2" @click="cancelEditing">Cancel</base-button>
          <base-button size="sm" @click="saveChanges" :loading="isSaving">Save</base-button>
        </div>
        <div v-if="!isEditing" class="tw-pb-2">
          <template v-for="group in groupedRxItems">
            <div v-for="(rxItem, index) in group.items" :key="rxItem.businessItemCategory.id || index" class="tw-mb-3">
              <div class="tw-text-xs tw-font-medium tw-text-gray-800">
                {{ group.productGroup }}
                <template v-if="group.items.length > 1">
                  {{ index + 1 }}
                </template>
              </div>
              <div class="tw-text-sm tw-text-gray-600" style="white-space: pre-wrap;">{{ rxItem.businessItemCategory.description.long }}</div>
            </div>
          </template>
        </div>
        <div class="tw-w-full tw-border-gray-200 tw-border-t"></div>
        <business-member-prescription-notes
          :notes="prescription.notes"
          class="tw-mt-4"
          :business-prescription-id="prescription.id"
          :user-id="userId"
          :businessId="businessId"
          @updated="$emit('updated')"
        />
      </div>
    </div>
    <business-member-prescription-change-status ref="changeStatusModal" :businessId="businessId" :businessPrescriptionId="prescription.id" @updated="$emit('updated')" />
    <business-member-prescription-delete-program ref="deleteProgramModal" :businessId="businessId" :businessPrescriptionId="prescription.id" @updated="$emit('updated')" />
    <assign-prescriber-modal ref="assignModal" :businessId="businessId" :businessPrescriberLocations="businessUsers" @update-supervising-prescriber="updateSupervisingPrescriber" />
  </div>
</template>

<script>
import BusinessMemberPrescriptionNotes from "@/views/business/member/prescriptions/components/BusinessMemberPrescriptionNotes";
import BusinessMemberPrescriptionChangeStatus from "@/views/business/member/prescriptions/components/BusinessMemberPrescriptionChangeStatus";
import BusinessMemberPrescriptionDeleteProgram from "@/views/business/member/prescriptions/components/BusinessMemberPrescriptionDeleteProgram";
import EditBusinessMemberPrescriptionMedications from "@/views/business/member/prescriptions/components/EditBusinessMemberPrescriptionMedications";
import EditBusinessMemberPrescriptionRxItems from "@/views/business/member/prescriptions/components/EditBusinessMemberPrescriptionRxItems";
import AssignPrescriberModal from "@/views/business/member/prescriptions/components/AssignPrescriberModal";
import { mapGetters } from "vuex";
import { capitalize, orderBy, uniq, groupBy } from "lodash";
import { MoreVerticalIcon } from "@/assets/svgs";
import { PRESCRIPTION_STATUS_OPTIONS } from "@/constants/constants";
import moment from "moment";

export default {
  components: {
    BusinessMemberPrescriptionNotes,
    BusinessMemberPrescriptionChangeStatus,
    BusinessMemberPrescriptionDeleteProgram,
    EditBusinessMemberPrescriptionMedications,
    MoreVerticalIcon,
    EditBusinessMemberPrescriptionRxItems,
    AssignPrescriberModal,
  },
  props: {
    businessId: String,
    userId: String,
    prescription: Object,
    rxItems: {
      type: Array,
      default: () => [],
    },
    businessUsers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      capitalize,
      newStatus: null,
      changeReason: null,
      isPDFRequesting: false,
      isLoading: false,
      isEditing: false,
      isSaving: false,
      editMedications: [],
      editRxItems: [],
      showAssignModal: false,
      deleteError: { errorTitle: "", errorMessage: "", error: null },
      changeError: {
        errorTitle: "",
        errorMessage: "",
        error: null,
      },
    };
  },
  computed: {
    ...mapGetters("location", ["locations"]),
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("role", ["isLocationFiltered", "isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isPscSecondaryRole"]),

    numMedications() {
      return this.prescription && this.prescription.medications ? this.prescription.medications.length : 0;
    },

    actionOptions() {
      let options = [];
      if (!this.prescription.status.isAccepted && this.isLocationFiltered === false && !this.prescription.status.isExpired) options.push("Invite to Plan");
      if (!this.prescription.status.isAccepted && this.isLocationFiltered === false && !this.prescription.status.isExpired) options.push("Create Plan");
      if (this.isLocationFiltered === false) options.push("Change Status");
      if (!this.isPscSecondaryRole) options.push("Update Prescriber");

      if (this.isLocationFiltered === false) options.push("Generate Rx Details PDF");
      if (
        !this.isEditing &&
        !this.prescription.status.isExpired &&
        (this.isBusinessManagement(this.organization.id) === true || this.isBusinessAdministrator(this.organization.id) === true || this.isPredictile() === true) &&
        !this.isPscSecondaryRole
      )
        options.push("Edit");
      if (this.canDelete === true && this.isLocationFiltered === false) options.push("Delete");

      return options;
    },
    allowedMedications() {
      if (!this.prescription.businessProgram || !this.prescription.businessProgram.allowedMedications) {
        return [];
      }
      return this.prescription.businessProgram.allowedMedications
        .map((am) => ({
          label: am.name.value,
          value: am.id,
          productGroup: am.productGroup,
        }))
        .filter((m) => !this.editMedications.find((em) => em.value === m.value));
    },
    allowedRxItems() {
      const currentProgram = this.prescription.businessProgram;

      if (!currentProgram || !this.rxItems || this.rxItems.length === 0) {
        return [];
      }

      const currentProgramId = currentProgram.id;
      const filtered = this.rxItems.filter((rxItem) => {
        const itemFilterType = rxItem.programFilterType;
        let itemProgramsIds = [];

        if (rxItem.programsId && typeof rxItem.programsId === "string") {
          try {
            itemProgramsIds = JSON.parse(rxItem.programsId);
          } catch (e) {
            itemProgramsIds = [];
          }
        } else if (Array.isArray(rxItem.programsId)) {
          itemProgramsIds = rxItem.programsId;
        }

        let isVisible = false;
        if (itemFilterType === null || itemFilterType === "all") {
          isVisible = true;
        } else if (itemFilterType === "include") {
          isVisible = itemProgramsIds.includes(currentProgramId);
        } else if (itemFilterType === "exclude") {
          isVisible = !itemProgramsIds.includes(currentProgramId);
        } else {
          isVisible = true;
        }
        return isVisible;
      });
      const mappedItems = filtered.map((item) => ({
        label: item.name,
        value: item.businessItemCategoryId,
        productGroup: item.productGroup,
      }));
      return mappedItems;
    },
    sortedAllowedMedications() {
      if (!this.allowedMedications) return [];
      const groups = orderBy(uniq(this.allowedMedications.map((m) => m.productGroup)));

      return groups.map((g) => ({
        label: g,
        options: orderBy(this.allowedMedications.filter((m) => m.productGroup === g), ["productGroup", "label"]),
      }));
    },
    orderedMedications() {
      return orderBy(this.prescription.medications, [(x) => x.sortOrder, (x) => x.businessItemCategory.name.value]);
    },
    groupedRxItems() {
      if (!this.prescription.rxItems || this.prescription.rxItems.length === 0) {
        return [];
      }

      const grouped = groupBy(this.prescription.rxItems, (item) => {
        const groupName = item.businessItemCategory && item.businessItemCategory.productGroup;
        return groupName || "Other Rx Items";
      });

      const result = Object.keys(grouped).map((groupName) => {
        const itemsInGroup = grouped[groupName];
        const sortedItemsInGroup = orderBy(itemsInGroup, [(x) => x.sortOrder, (x) => x.businessItemCategory && x.businessItemCategory.name && x.businessItemCategory.name.value]);
        return {
          productGroup: groupName,
          items: sortedItemsInGroup,
        };
      });
      const finalSortedGroups = orderBy(result, ["productGroup"]);

      return finalSortedGroups;
    },
    canDelete() {
      if (this.isPredictile() === true || this.isBusinessManagement(this.businessId) === true || this.isBusinessAdministrator(this.businessId) === true) return true;
      else return false;
    },
    moreAllowedMedications() {
      return this.prescription.businessProgram.maxMedications > this.numMedications;
    },
    status() {
      return this.prescription.isDeleted ? "deleted" : this.prescription.status.value;
    },
    dynamicLabel() {
      const label = this.prescription && this.prescription.businessProgram && this.prescription.businessProgram.programFieldLabel;
      return label ? label.charAt(0).toUpperCase() + label.slice(1) : "Medication";
    },
  },
  watch: {},
  async created() {},
  methods: {
    formatDate(date) {
      return new moment.utc(date).local().format("MM/DD/YYYY hh:mm a");
    },
    getLocation(prescription) {
      const location = this.locations.find((l) => l.businessLocationId === prescription.businessLocationId);
      return location ? location.name : "";
    },
    actionLink(label) {
      switch (label) {
        case "Invite to Plan":
          this.inviteToPlan();
          break;
        case "Create Plan":
          this.createNewPlan();
          break;
        case "Change Status":
          this.changeStatus();
          break;
        case "Update Prescriber":
          this.showAssignModal = true;
          this.$nextTick(() => {
            this.$refs.assignModal.show(this.prescription);
          });
          break;
        case "Generate Rx Details PDF":
          this.generateMemberPDF();
          break;
        case "Delete":
          this.del();
          break;
        case "Edit":
          this.startEditing();
          break;
      }
    },
    inviteToPlan() {
      this.$emit("show-invite-modal", this.prescription);
    },
    createNewPlan() {
      this.$emit("show-create-modal", this.prescription);
    },
    changeStatus() {
      this.$refs.changeStatusModal.show(this.prescription.status.value);
    },
    del() {
      this.$refs.deleteProgramModal.show(this.prescription.businessProgram.name);
    },
    generateMemberPDF() {
      this.isPDFRequesting = true;
      this.$axios
        .post(`/businesses/${this.businessId}/users/${this.userId}/prescriptions/${this.prescription.id}/rxdetails/pdf`)
        .then(() => {
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "PDF Queued",
            message: `The PDF for ${this.prescription.businessProgram.name} was requested. This may take a minute to add the to the Member Documents tab.`,
          });
          this.hide();
        })
        .catch((err) => {
          this.errorTitle = `Error requesting PDF.`;
          this.errorMessage = "Sorry, we had trouble submitting the request this PDF. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isPDFRequesting = false;
        });
    },
    getPrescriptionStatus(status) {
      return PRESCRIPTION_STATUS_OPTIONS.find((s) => s.value === status).label;
    },
    startEditing() {
      this.editMedications = (this.orderedMedications || []).map((m) => ({
        label: m.businessItemCategory.name.value,
        value: m.businessItemCategory.id,
        productGroup: m.businessItemCategory.productGroup,
        sortOrder: m.sortOrder,
      }));
      this.editRxItems = (this.prescription.rxItems || [])
        .map((rx) => {
          const mappedRx = {
            label: rx.businessItemCategory && rx.businessItemCategory.name ? rx.businessItemCategory.name.value : "",
            value: rx.businessItemCategory ? rx.businessItemCategory.id : "",
            productGroup: rx.businessItemCategory ? rx.businessItemCategory.productGroup : "",
          };
          if (mappedRx.label === null || mappedRx.label === undefined) mappedRx.label = "";
          if (mappedRx.value === null || mappedRx.value === undefined || mappedRx.value === "") mappedRx.value = null;
          return mappedRx;
        })
        .filter((item) => item.value !== null);
      this.$nextTick(() => {
        this.isEditing = true;
      });
    },
    cancelEditing() {
      this.isEditing = false;
      this.editMedications = [];
      this.editRxItems = [];
    },
    async saveChanges() {
      this.isSaving = true;

      try {
        await Promise.all([this.savePrescriptionItemsChanges()]);

        this.isEditing = false;
        this.$emit("updated");
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Error saving changes",
          message: "There was a problem updating the prescription details. Please reload the page to try again.",
          error,
        });
      } finally {
        this.isSaving = false;
      }
    },
    async savePrescriptionItemsChanges() {
      const oldMedicationIds = (this.orderedMedications || []).map((m) => m.businessItemCategory.id).filter((id) => id);
      const oldRxItemIds = (this.prescription.rxItems || []).map((rx) => rx.businessItemCategory.id).filter((id) => id);
      const newMedicationIds = this.editMedications.map((m) => m.value).filter((id) => id);
      const newRxItemIds = this.editRxItems.map((rx) => rx.value).filter((id) => id);

      const medicationsChanged = oldMedicationIds.length !== newMedicationIds.length || oldMedicationIds.some((id, i) => id !== newMedicationIds[i]);
      const rxItemsChanged = oldRxItemIds.length !== newRxItemIds.length || oldRxItemIds.some((id, i) => id !== newRxItemIds[i]);

      if (medicationsChanged || rxItemsChanged) {
        try {
          const payload = {
            businessPrescriptionId: this.prescription.id,
            businessItemCategoryIds: newMedicationIds,
            rxItemCategoryIds: newRxItemIds,
          };
          await this.$axios.post(`/businesses/${this.organization.id}/members/${this.userId}/prescriptions/items`, payload);
        } catch (error) {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error saving prescription items",
            message: "We had a problem updating these items. Please reload the page to try again.",
            error,
          });
          throw error;
        }
      }
    },
    statusColor(status) {
      switch (status.toLowerCase()) {
        case "deleted":
          return "gray";
        case "expired":
          return "gray";
        default:
          return "green";
      }
    },

    updateSupervisingPrescriber(locationId) {
      // Emit up to parent business
      this.$emit("update-supervising-prescriber", { prescriptionId: this.prescription.id, locationId });
    },
  },
};
</script>
